import { useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { FileText, Plus, Trash2, Download, Settings, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS_RU = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const fmtMoney = (n) => (Number(n) || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDateRu = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]} ${d.getFullYear()} г.`;
};

// ---------------------------------------------------------------------------
// A4 canvas renderer → jsPDF. Canvas text = perfect Cyrillic without fonts.
// ---------------------------------------------------------------------------
async function renderInvoicePdf(inv) {
  const S = 8; // px per mm
  const W = 210 * S, H = 297 * S;
  const M = 15 * S; // margin
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "alphabetic";
  const F = (size, weight = 400) => { ctx.font = `${weight} ${size * S}px Arial, sans-serif`; };
  const line = (x1, y1, x2, y2, w = 1) => {
    ctx.beginPath(); ctx.lineWidth = w; ctx.strokeStyle = "#000";
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  };
  const wrap = (text, maxWidth) => {
    const words = String(text).split(/\s+/); const lines = []; let cur = "";
    for (const w of words) {
      const t = cur ? cur + " " + w : w;
      if (ctx.measureText(t).width > maxWidth && cur) { lines.push(cur); cur = w; } else cur = t;
    }
    if (cur) lines.push(cur);
    return lines;
  };
  const r = inv.requisites || {};
  let y = M;

  // --- Payment order sample (beneficiary table) ---
  F(3, 400);
  ctx.fillText("Образец платежного поручения", M, y); y += 2 * S;
  const tW = W - 2 * M;
  const c1 = M, c2 = M + tW * 0.52, c3 = M + tW * 0.80, cEnd = M + tW;
  const rowH = 7 * S;
  // header row 1: Бенефициар | ИИК | КБе
  const drawCell = (x1, x2, yTop, label, value, boldVal = false) => {
    F(2.6, 400); ctx.fillText(label, x1 + 1.5 * S, yTop + 3 * S);
    F(3, boldVal ? 700 : 400);
    const lines = wrap(value || "", x2 - x1 - 3 * S);
    lines.slice(0, 2).forEach((ln, i) => ctx.fillText(ln, x1 + 1.5 * S, yTop + (5.7 + i * 3.4) * S));
  };
  // row 1
  let ty = y;
  drawCell(c1, c2, ty, "Бенефициар:", `${r.company_name || ""}  БИН: ${r.bin || ""}`, true);
  drawCell(c2, c3, ty, "ИИК", r.iik || "", true);
  drawCell(c3, cEnd, ty, "КБе", r.kbe || "", true);
  line(c1, ty, cEnd, ty); line(c1, ty + rowH, cEnd, ty + rowH);
  line(c1, ty, c1, ty + rowH * 2); line(c2, ty, c2, ty + rowH * 2); line(c3, ty, c3, ty + rowH * 2); line(cEnd, ty, cEnd, ty + rowH * 2);
  // row 2
  ty += rowH;
  drawCell(c1, c2, ty, "Банк бенефициара:", r.bank || "", true);
  drawCell(c2, c3, ty, "БИК", r.bik || "", true);
  drawCell(c3, cEnd, ty, "Код назначения платежа", r.knp || "");
  line(c1, ty + rowH, cEnd, ty + rowH);
  y = ty + rowH + 8 * S;

  // --- Title ---
  F(4.6, 700);
  ctx.fillText(`Счет на оплату № ${inv.number} от ${fmtDateRu(inv.date)}`, M, y);
  y += 2 * S; line(M, y, W - M, y, 3); y += 7 * S;

  // --- Supplier / buyer ---
  const labelW = 26 * S;
  const infoRow = (label, value, bold) => {
    F(3, 400); ctx.fillText(label, M, y);
    F(3, bold ? 700 : 400);
    const lines = wrap(value, W - M - (M + labelW));
    lines.forEach((ln, i) => { ctx.fillText(ln, M + labelW, y); if (i < lines.length - 1) y += 4 * S; });
    y += 5.5 * S;
  };
  infoRow("Поставщик:", `${r.company_name || ""}, БИН ${r.bin || ""}, ${r.address || ""}${r.phone ? ", тел.: " + r.phone : ""}`, true);
  infoRow("Покупатель:", `${inv.buyer_name}${inv.buyer_bin ? ", БИН/ИИН " + inv.buyer_bin : ""}${inv.buyer_phone ? ", тел.: " + inv.buyer_phone : ""}`, true);
  infoRow("Основание:", inv.basis || "Без договора", false);
  y += 2 * S;

  // --- Items table ---
  const cols = [
    { key: "num", label: "№", w: 0.045, align: "center" },
    { key: "name", label: "Наименование товара (работ, услуг)", w: 0.395, align: "left" },
    { key: "qty", label: "Кол-во", w: 0.075, align: "center" },
    { key: "unit", label: "Ед.", w: 0.055, align: "center" },
    { key: "price", label: "Цена, ₸", w: 0.13, align: "right" },
    { key: "disc", label: "Скидка", w: 0.08, align: "center" },
    { key: "sum", label: "Сумма, ₸", w: 0.22, align: "right" },
  ];
  const colX = []; let acc = M;
  cols.forEach(c => { colX.push(acc); acc += tW * c.w; });
  colX.push(W - M);
  // header
  const thH = 8 * S;
  ctx.fillStyle = "#f2f2f2"; ctx.fillRect(M, y, tW, thH); ctx.fillStyle = "#000";
  F(2.9, 700);
  cols.forEach((c, i) => {
    const cx = c.align === "left" ? colX[i] + 1.5 * S : c.align === "right" ? colX[i + 1] - 1.5 * S : (colX[i] + colX[i + 1]) / 2;
    ctx.textAlign = c.align === "left" ? "left" : c.align === "right" ? "right" : "center";
    ctx.fillText(c.label, cx, y + 5.2 * S);
  });
  ctx.textAlign = "left";
  line(M, y, W - M, y, 2);
  line(M, y + thH, W - M, y + thH, 2);
  let tableTop = y; y += thH;
  // rows
  (inv.items || []).forEach((it, idx) => {
    F(3, 400);
    const nameLines = wrap(it.name, tW * cols[1].w - 3 * S);
    const rh = Math.max(7 * S, (nameLines.length * 3.8 + 3.2) * S);
    const discPct = Number(it.discount_pct || 0);
    const discLabel = discPct > 0 ? `-${discPct}%` : "—";
    const vals = [String(idx + 1), null, String(it.qty), it.unit || "шт",
                    fmtMoney(it.price), discLabel, fmtMoney(it.sum)];
    cols.forEach((c, i) => {
      if (i === 1) return;
      const cx = c.align === "right" ? colX[i + 1] - 1.5 * S : (colX[i] + colX[i + 1]) / 2;
      ctx.textAlign = c.align === "right" ? "right" : "center";
      ctx.fillText(vals[i], cx, y + 4.6 * S);
    });
    ctx.textAlign = "left";
    nameLines.forEach((ln, i) => ctx.fillText(ln, colX[1] + 1.5 * S, y + (4.6 + i * 3.8) * S));
    line(M, y + rh, W - M, y + rh);
    y += rh;
  });
  // vertical lines
  colX.forEach((x) => line(x, tableTop, x, y));
  // totals block (multi-line: subtotal / base discount / total to pay
  // and optional flash 24-h offer).
  const rightCol = colX[cols.length]; // right edge of table
  const labelCol = colX[cols.length - 1]; // last column starts at "Сумма"
  const subtotal = Number(inv.subtotal || inv.total);
  const baseDisc = Number(inv.base_discount_amount || 0);
  const total = Number(inv.total || 0);
  const flashPct = Number(inv.flash_discount_pct || 0);
  const flashAmt = Number(inv.flash_discount_amount || 0);
  const flashTot = Number(inv.flash_total || total);

  const drawTotalRow = (label, value, bold, tone) => {
    F(3.2, bold ? 700 : 400);
    if (tone) ctx.fillStyle = tone;
    ctx.textAlign = "right";
    ctx.fillText(label, labelCol - 1.5 * S, y + 5 * S);
    ctx.fillText(value, rightCol - 1.5 * S, y + 5 * S);
    if (tone) ctx.fillStyle = "#000";
    ctx.textAlign = "left";
    y += 6 * S;
  };

  if (baseDisc > 0) {
    drawTotalRow("Полная стоимость:", fmtMoney(subtotal), false, "#6b7280");
    drawTotalRow(`Скидка:`, `-${fmtMoney(baseDisc)}`, false, "#047857");
  }
  drawTotalRow("Итого к оплате:", fmtMoney(total), true);
  y += 3 * S;

  const count = (inv.items || []).length;
  F(3, 400);
  ctx.fillText(`Всего наименований ${count}, на сумму ${fmtMoney(total)} KZT`, M, y); y += 4.6 * S;
  F(3.2, 700);
  ctx.fillText(`Всего к оплате: ${inv.total_words}`, M, y); y += 6 * S;

  // Flash 24-h offer box — highlighted so it visually pops on the printed PDF.
  if (flashPct > 0 && flashAmt > 0) {
    const boxTop = y;
    const boxH = 22 * S;
    // amber tint background
    ctx.fillStyle = "#FEF3C7";
    ctx.fillRect(M, boxTop, W - 2 * M, boxH);
    ctx.strokeStyle = "#D97706"; ctx.lineWidth = 2;
    ctx.strokeRect(M, boxTop, W - 2 * M, boxH);
    ctx.fillStyle = "#7C2D12";
    F(3.5, 700);
    ctx.fillText("⚡ Специальное предложение: оплата в течение 24 часов", M + 3 * S, boxTop + 5.5 * S);
    F(3, 400);
    const deadline = inv.flash_deadline_at
      ? new Date(inv.flash_deadline_at).toLocaleString("ru-RU",
          { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })
      : "";
    if (deadline) {
      ctx.fillText(`Оплатите счёт до ${deadline} — и получите дополнительную скидку ${flashPct}%.`,
                    M + 3 * S, boxTop + 10 * S);
    } else {
      ctx.fillText(`При оплате в течение 24 ч — дополнительная скидка ${flashPct}%.`,
                    M + 3 * S, boxTop + 10 * S);
    }
    F(3.6, 700);
    ctx.fillText(`Экономия: ${fmtMoney(flashAmt)} ₸`, M + 3 * S, boxTop + 15 * S);
    ctx.fillText(`К оплате сегодня: ${fmtMoney(flashTot)} ₸`,
                  W - M - 3 * S - ctx.measureText(`К оплате сегодня: ${fmtMoney(flashTot)} ₸`).width,
                  boxTop + 15 * S);
    // reset
    ctx.fillStyle = "#000";
    y = boxTop + boxH + 4 * S;
  }
  y += 2 * S;

  line(M, y, W - M, y, 2); y += 6 * S;
  F(2.7, 400);
  ctx.fillStyle = "#333";
  wrap("Внимание! Оплата данного счета означает согласие с условиями поставки товара. Счет действителен в течение 3 (трёх) банковских дней. Уведомление об оплате обязательно, в противном случае не гарантируется наличие товара.", W - 2 * M)
    .forEach(ln => { ctx.fillText(ln, M, y); y += 3.8 * S; });
  ctx.fillStyle = "#000";
  y += 8 * S;

  // --- Executor + stamp ---
  F(3.2, 700);
  ctx.fillText(`Исполнитель: _______________________ ${r.executor ? "/ " + r.executor + " /" : ""}`, M, y);
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im); im.onerror = rej;
      im.src = "/stamp-italy-retail.jpg";
    });
    const stampSize = 42 * S;
    ctx.globalAlpha = 0.9;
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(img, W - M - stampSize - 8 * S, y - 20 * S, stampSize, stampSize);
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
  } catch (e) { /* stamp missing — skip */ }

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  pdf.addImage(canvas.toDataURL("image/jpeg", 0.93), "JPEG", 0, 0, 210, 297, undefined, "FAST");
  pdf.save(`Счет_${inv.number}.pdf`);
}

// ---------------------------------------------------------------------------
export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showReq, setShowReq] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/billing/invoices`, { withCredentials: true });
      setInvoices(r.data);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const download = async (inv) => {
    setDownloading(inv.id);
    try { await renderInvoicePdf(inv); } finally { setDownloading(null); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Удалить счёт ${inv.number}?`)) return;
    await axios.delete(`${API}/admin/billing/invoices/${inv.id}`, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="admin-invoices-page">
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
        <div style={{ display: "flex", gap: "8px" }}>
          <button data-testid="requisites-btn" onClick={() => setShowReq(true)}
            style={{ background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: "6px", padding: "9px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Settings size={14} /> Реквизиты
          </button>
          <button data-testid="new-invoice-btn" onClick={() => setShowForm(true)}
            style={{ background: "#0A0A0A", color: "#D4B878", border: "1px solid #C5A059", borderRadius: "6px", padding: "9px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Plus size={14} /> Новый счёт
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#888", fontFamily: "Manrope" }}>Загрузка…</div>
      ) : invoices.length === 0 ? (
        <div style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: "10px", padding: "48px", textAlign: "center", fontFamily: "Manrope", color: "#6b7280" }}>
          <FileText size={32} style={{ margin: "0 auto 12px", display: "block", color: "#C5A059" }} />
          Счетов пока нет. Заполните <b>Реквизиты</b> (один раз) и создайте первый счёт.
        </div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#fafaf9", textAlign: "left" }}>
                {["№ счёта", "Дата", "Покупатель", "Сумма, ₸", "Автор", ""].map(h => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 14px", fontWeight: 700 }}>{inv.number}</td>
                  <td style={{ padding: "10px 14px" }}>{fmtDateRu(inv.date)}</td>
                  <td style={{ padding: "10px 14px" }}>{inv.buyer_name}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 600 }}>{fmtMoney(inv.total)}</td>
                  <td style={{ padding: "10px 14px", color: "#9ca3af", fontSize: "12px" }}>{inv.created_by_email}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => download(inv)} disabled={downloading === inv.id}
                      title="Скачать PDF"
                      style={{ background: "#0A0A0A", color: "#D4B878", border: "none", borderRadius: "5px", padding: "6px 10px", cursor: "pointer", marginRight: "6px", display: "inline-flex", alignItems: "center", gap: "5px", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700 }}>
                      {downloading === inv.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
                    </button>
                    <button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)} title="Удалить"
                      style={{ background: "white", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "5px", padding: "6px 8px", cursor: "pointer" }}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <InvoiceForm onClose={(created) => { setShowForm(false); if (created) { load(); renderInvoicePdf(created); } }} />}
      {showReq && <RequisitesModal onClose={() => setShowReq(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function InvoiceForm({ onClose }) {
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_bin: "", buyer_phone: "", basis: "Без договора" });
  const [items, setItems] = useState([{ name: "", qty: 1, unit: "шт", price: "", discount_pct: "" }]);
  const [flashPct, setFlashPct] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Numeric helpers with graceful fallbacks for empty strings.
  const n = (v) => parseFloat(v) || 0;
  const setItem = (idx, key, val) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));

  const rowSubtotal = (it) => n(it.qty) * n(it.price);
  const rowTotal = (it) => rowSubtotal(it) * (1 - Math.min(100, Math.max(0, n(it.discount_pct))) / 100);

  const subtotal = items.reduce((s, it) => s + rowSubtotal(it), 0);
  const total = items.reduce((s, it) => s + rowTotal(it), 0);
  const baseDiscount = subtotal - total;
  const flashPctSafe = Math.min(10, Math.max(0, n(flashPct)));
  const flashAmount = total * flashPctSafe / 100;
  const flashTotal = total - flashAmount;

  const valid = buyer.buyer_name.trim() && items.some(i => i.name.trim() && n(i.qty) > 0 && n(i.price) > 0);

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/billing/invoices`, {
        ...buyer,
        flash_discount_pct: flashPctSafe,
        items: items.filter(i => i.name.trim()).map(i => ({
          name: i.name.trim(),
          qty: n(i.qty),
          unit: i.unit || "шт",
          price: n(i.price),
          discount_pct: Math.min(100, Math.max(0, n(i.discount_pct))),
        })),
      }, { withCredentials: true });
      onClose(r.data);
      return;
    } catch (e) {
      setError(e.response?.data?.detail || "Ошибка создания счёта");
    }
    setSaving(false);
  };

  const inp = { width: "100%", background: "white", border: "1px solid #e5e7eb", borderRadius: "5px", padding: "9px 11px", fontFamily: "Manrope", fontSize: "13px", outline: "none" };
  const lbl = { display: "block", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "5px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }} onClick={() => onClose(null)}>
      <div data-testid="invoice-form-modal" onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "820px", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400 }}>Новый счёт на оплату</h3>
          <button onClick={() => onClose(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "10px", marginBottom: "12px" }}>
          <div>
            <label style={lbl}>Покупатель *</label>
            <input data-testid="invoice-buyer-name" style={inp} value={buyer.buyer_name} onChange={e => setBuyer({ ...buyer, buyer_name: e.target.value })} placeholder="ФИО / ТОО / ИП" />
          </div>
          <div>
            <label style={lbl}>БИН / ИИН</label>
            <input data-testid="invoice-buyer-bin" style={inp} value={buyer.buyer_bin} onChange={e => setBuyer({ ...buyer, buyer_bin: e.target.value })} placeholder="Необязательно" />
          </div>
          <div>
            <label style={lbl}>Телефон</label>
            <input data-testid="invoice-buyer-phone" style={inp} value={buyer.buyer_phone} onChange={e => setBuyer({ ...buyer, buyer_phone: e.target.value })} placeholder="+7 ..." />
          </div>
        </div>
        <div style={{ marginBottom: "14px" }}>
          <label style={lbl}>Основание</label>
          <input data-testid="invoice-basis" style={inp} value={buyer.basis} onChange={e => setBuyer({ ...buyer, basis: e.target.value })} />
        </div>

        <label style={lbl}>Позиции *</label>
        <div style={{ display: "grid", gridTemplateColumns: "3fr 60px 60px 110px 80px 110px 28px", gap: "6px", marginBottom: "4px",
                       fontFamily: "Manrope", fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <div>Наименование</div><div>Кол.</div><div>Ед.</div><div>Цена, ₸</div><div>Скидка %</div>
          <div style={{ textAlign: "right" }}>К оплате, ₸</div><div></div>
        </div>
        {items.map((it, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 60px 60px 110px 80px 110px 28px", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
            <input data-testid={`invoice-item-name-${idx}`} style={inp} value={it.name} onChange={e => setItem(idx, "name", e.target.value)} placeholder="Наименование товара" />
            <input data-testid={`invoice-item-qty-${idx}`} style={inp} type="number" min="0" value={it.qty} onChange={e => setItem(idx, "qty", e.target.value)} placeholder="Кол." />
            <input style={inp} value={it.unit} onChange={e => setItem(idx, "unit", e.target.value)} placeholder="шт" />
            <input data-testid={`invoice-item-price-${idx}`} style={inp} type="number" min="0" value={it.price} onChange={e => setItem(idx, "price", e.target.value)} placeholder="Полная" />
            <input data-testid={`invoice-item-disc-${idx}`} style={inp} type="number" min="0" max="100" value={it.discount_pct} onChange={e => setItem(idx, "discount_pct", e.target.value)} placeholder="0" />
            <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 700, textAlign: "right" }}>
              {fmtMoney(rowTotal(it))}
            </div>
            <button onClick={() => setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
          </div>
        ))}
        <button data-testid="invoice-add-item-btn" onClick={() => setItems(prev => [...prev, { name: "", qty: 1, unit: "шт", price: "", discount_pct: "" }])}
          style={{ background: "white", border: "1px dashed #C5A059", color: "#8a6d2a", borderRadius: "5px", padding: "7px 12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, marginBottom: "16px" }}>
          + Добавить позицию
        </button>

        {/* Flash 24h discount */}
        <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "8px",
                       padding: "12px 14px", marginBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: "13px", color: "#92400E" }}>
              ⚡ Скидка за оплату в течение 24 ч
            </div>
            <input data-testid="invoice-flash-pct" type="number" min="0" max="10" step="0.5"
              value={flashPct} onChange={(e) => setFlashPct(e.target.value)}
              placeholder="0"
              style={{ ...inp, width: 90, background: "white" }} />
            <span style={{ fontFamily: "Manrope", fontSize: "12px", color: "#92400E" }}>%
              &nbsp;— макс. 10%. Активна, если клиент оплатит до&nbsp;
              {new Date(Date.now() + 24 * 3600 * 1000).toLocaleString("ru-RU",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>

        {/* Totals summary */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
          <div data-testid="invoice-totals-panel"
            style={{ minWidth: 320, background: "#fafaf9", borderRadius: "8px",
                     padding: "12px 16px", border: "1px solid #e5e7eb",
                     fontFamily: "Manrope", fontSize: "13px" }}>
            <SumRow label="Полная стоимость" value={fmtMoney(subtotal) + " ₸"} muted />
            {baseDiscount > 0 && (
              <SumRow label={`Скидка`}
                      value={"−" + fmtMoney(baseDiscount) + " ₸"} tone="#059669" />
            )}
            <SumRow label="К оплате" value={fmtMoney(total) + " ₸"} bold />
            {flashPctSafe > 0 && (
              <>
                <div style={{ height: 1, background: "#e5e7eb", margin: "8px 0" }} />
                <SumRow label={`⚡ Экстра-скидка (${flashPctSafe}%) при оплате в 24ч`}
                        value={"−" + fmtMoney(flashAmount) + " ₸"} tone="#B45309" />
                <SumRow label="К оплате сегодня"
                        value={fmtMoney(flashTotal) + " ₸"} bold tone="#B45309" />
              </>
            )}
          </div>
        </div>
        {error && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#DC2626", marginBottom: "10px" }}>{error}</div>}
        <div style={{ display: "flex", gap: "8px" }}>
          <button data-testid="invoice-submit-btn" onClick={submit} disabled={!valid || saving}
            style={{ background: "#0A0A0A", color: "#D4B878", border: "1px solid #C5A059", borderRadius: "6px", padding: "11px 18px", cursor: valid ? "pointer" : "not-allowed", opacity: !valid || saving ? 0.5 : 1, fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {saving ? "Создание…" : "Создать и скачать PDF"}
          </button>
          <button onClick={() => onClose(null)} style={{ background: "white", border: "1px solid #d1d5db", borderRadius: "6px", padding: "11px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>Отмена</button>
        </div>
      </div>
    </div>
  );
}

// Simple totals row used in the invoice form summary panel.
function SumRow({ label, value, muted, bold, tone }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0",
                   color: tone || (muted ? "#6b7280" : "#0A0A0A"),
                   fontWeight: bold ? 700 : 500,
                   fontSize: bold ? "14px" : "12.5px" }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RequisitesModal({ onClose }) {
  const [req, setReq] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/billing/requisites`, { withCredentials: true }).then(r => setReq(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/billing/requisites`, req, { withCredentials: true });
      setSaved(true);
      setTimeout(() => onClose(), 700);
    } finally { setSaving(false); }
  };

  const fields = [
    ["company_name", "Наименование (ТОО)"],
    ["bin", "БИН"],
    ["address", "Юридический адрес"],
    ["phone", "Телефон"],
    ["iik", "ИИК (IBAN, KZ…)"],
    ["bank", "Банк бенефициара"],
    ["bik", "БИК банка"],
    ["kbe", "КБе"],
    ["knp", "КНП (код назначения платежа)"],
    ["executor", "Исполнитель (ФИО для подписи)"],
  ];
  const inp = { width: "100%", background: "white", border: "1px solid #e5e7eb", borderRadius: "5px", padding: "9px 11px", fontFamily: "Manrope", fontSize: "13px", outline: "none" };
  const lbl = { display: "block", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "5px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }} onClick={onClose}>
      <div data-testid="requisites-modal" onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: "10px", padding: "24px", width: "100%", maxWidth: "560px", maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400 }}>Реквизиты компании</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "6px", padding: "8px 10px", marginBottom: "14px" }}>
          Заполняется один раз — подставляется во все счета. Печать ТОО «ITALY RETAIL» добавляется на PDF автоматически.
        </div>
        {!req ? <div style={{ fontFamily: "Manrope", color: "#888" }}>Загрузка…</div> : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "14px" }}>
              {fields.map(([key, label]) => (
                <div key={key} style={{ gridColumn: ["company_name", "address", "iik", "bank"].includes(key) ? "1/-1" : "auto" }}>
                  <label style={lbl}>{label}</label>
                  <input data-testid={`req-${key}`} style={inp} value={req[key] || ""} onChange={e => setReq({ ...req, [key]: e.target.value })} />
                </div>
              ))}
            </div>
            <button data-testid="req-save-btn" onClick={save} disabled={saving}
              style={{ background: saved ? "#047857" : "#0A0A0A", color: saved ? "white" : "#D4B878", border: "1px solid #C5A059", borderRadius: "6px", padding: "11px 18px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              {saved ? "Сохранено ✓" : saving ? "Сохранение…" : "Сохранить реквизиты"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
